// @ts-check

import dateformat from 'dateformat';
import { formatCurrency } from './formatText';

/**
 * @import {
 *   InstallmentPlan,
 *   OfferDetail,
 *   SelectedInstallmentPlanDetail,
 *   CpcPaymentData,
 *   TransformedCpcData,
 *   ReviewData,
 *   PaymentPayload,
 *   PaymentSummary
 * } from '../types/state/installmentPlansState';
 */

export const DEFAULT_INSTALLMENT_PLAN_DEPOSIT = 50;
export const SERVICES_CATEGORY = 'Services';
const SUPPORTED_INSTALLMENTS = [12, 24];

/**
 * Determines whether the user is eligible for installment plans.
 *
 * @param {InstallmentPlan | null} plan
 * @returns {boolean}
 */
export const isInstallmentPlanEligible = plan => plan?.eligibility === true;

/**
 * @param {OfferDetail[]} details
 * @returns {SelectedInstallmentPlanDetail[]}
 */
export const getInstallmentPlans = details => details
  .filter(detail => SUPPORTED_INSTALLMENTS.includes(Number(detail.installments)))
  .map(detail => ({
    installments: Number(detail.installments),
    ltipOfferId: detail.ltipOfferId,
    amount: Number.parseFloat(detail.amount),
    planTermStartDate: detail.planTermStartDate,
    planTermEndDate: detail.planTermEndDate,
  }))
  .sort((a, b) => a.installments - b.installments);

/**
 * Builds the review-page model from the selected offer, bill, and CPC data.
 * @param {Object} params
 * @param {number} params.depositAmount - One-time payment deposit amount
 * @param {TransformedCpcData|null} params.paymentInfo - Pre-transformed CPC
 *   payment data if deposit > 0 (transformed via cpcDataToPaymentFormData)
 * @param {SelectedInstallmentPlanDetail} params.selectedPlanDetail - Selected
 *   plan
 * @param {number} params.pastDueBalance - Unpaid balance to be enrolled
 * @returns {ReviewData} Review page data model with all necessary display
 *   fields
 */
export const getInstallmentReviewData = ({
  depositAmount,
  paymentInfo,
  selectedPlanDetail,
  pastDueBalance,
}) => {
  // Validate and normalize numeric inputs; return default value if invalid
  const numDeposit = Number(depositAmount);
  const normalizedDeposit = Number.isFinite(numDeposit)
    ? numDeposit
    : 0;

  const numPastDue = Number(pastDueBalance);
  const normalizedPastDue = Number.isFinite(numPastDue)
    ? numPastDue
    : 0;

  // Build term and dateRange only if selectedPlanDetail exists
  let term = '';
  let dateRange = '';
  if (selectedPlanDetail) {
    // Coerce installments to number to handle string payloads from API
    const installments = Number(selectedPlanDetail.installments);
    if (Number.isFinite(installments)) {
      term = `${installments} months`;
      const startDate = selectedPlanDetail.planTermStartDate || 'TBD';
      const endDate = selectedPlanDetail.planTermEndDate || 'TBD';
      dateRange = `${startDate} - ${endDate}`;
    }
  }

  const reviewData = {
    depositAmount: normalizedDeposit,
    paymentInfo,
    planInfo: {
      ltipOfferId: selectedPlanDetail?.ltipOfferId || null,
      term,
      dateRange,
      pastDueBalance: formatCurrency(normalizedPastDue),
    },
    selectedPlanDetail,
  };

  return reviewData;
};

/**
 * Transform CPC payment data into PrismPaymentSummary format.
 * Consolidates payment method details (card or bank) and customer contact info.
 * Supports both raw CPC format and pre-transformed data format.
 *
 * @param {CpcPaymentData|TransformedCpcData|null} paymentData - CPC response
 *   or transformed payment data
 * @param {number} depositAmount - One-time payment deposit amount
 * @param {string} [dateOverride] - Optional fixed date for testing
 *   (YYYY-MM-DD format)
 * @returns {PaymentSummary|null} Formatted payment summary for
 *   PrismPaymentSummary component
 */
export const transformCpcToPaymentSummary = (paymentData, depositAmount, dateOverride) => {
  if (!paymentData) return null;

  const summary = {
    amount: depositAmount,
    date: dateOverride || dateformat(new Date(), 'yyyy-mm-dd'),
  };

  // If paymentData has paymentMethodOption (transformed format), use it directly
  if (paymentData.paymentMethodOption) {
    summary.paymentMethodOption = paymentData.paymentMethodOption;
    return summary;
  }

  // Otherwise, extract from raw CPC format (legacy path for compatibility)
  // Cast to CpcPaymentData since we've confirmed it's not TransformedCpcData
  const cpcData = /** @type {CpcPaymentData} */(paymentData);

  // Extract email from customer details with fallback paths for robustness
  // Use explicit null/undefined checks instead of optional chaining
  let email = null;
  const { customerDetails } = cpcData;

  if (customerDetails && customerDetails.contact) {
    ({ emailAddress: email } = customerDetails.contact);
  }

  if (!email) {
    const { channelData } = cpcData;
    if (channelData && channelData.customerDetails) {
      ({ email } = channelData.customerDetails);
    }
  }

  if (email) {
    summary.email = email;
  }

  // Safely normalize payment method type: guard with typeof check before calling
  // string methods
  const paymentType = cpcData.paymentMethodOption?.type;
  const optionType = typeof paymentType === 'string'
    ? paymentType.toUpperCase().trim()
    : '';
  if (optionType === 'APPLEPAY') {
    summary.paymentMethodOption = cpcData.paymentMethodOption;
    return summary;
  }

  // Prioritize extracted card/bank details over CPC's paymentMethodOption
  // Ensures last4/expirationDate are included when available
  if (cpcData.cardDetails && cpcData.cardDetails.token) {
    const { cardDetails } = cpcData;
    const cardType = cardDetails.niceCardType || cardDetails.cardType || 'Card';
    const last4 = cardDetails.cardLast4Digits
      || cardDetails.maskedCardNumber
      || '';
    summary.paymentMethodOption = {
      type: 'PaymentCard',
      cardType,
      last4DigitsCardNumber: last4,
      expirationDate: cardDetails.expirationDate || '',
    };
  } else if (cpcData.bankDetails && cpcData.bankDetails.token) {
    const acctNum = cpcData.bankDetails.maskedAccountNumber
      || cpcData.bankDetails.bankAccountLast4Digits
      || '';
    summary.paymentMethodOption = {
      type: 'Bank',
      bankAccountType: cpcData.bankDetails.bankAccountType
        || cpcData.bankDetails.accountType
        || 'Bank Account',
      bankAccountNumber: acctNum,
    };
  } else if (cpcData.paymentMethodOption) {
    // Fall back to CPC's pre-formatted option if no card/bank token available
    summary.paymentMethodOption = cpcData.paymentMethodOption;
  }

  return summary;
};
